import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  searchRow: { display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" },
  filterSelect: { padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px", minWidth: "140px" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }
};

const BLANK_FORM = {
  firstName: "", lastName: "", email: "", phone: "",
  programmeId: "", graduationYear: "", currentEmployer: "", jobTitle: ""
};

class AlumniList extends Component {
  state = {
    tab: 0,
    alumni: [], total: 0, page: 1, totalPages: 1,
    search: "", programme: "", graduationYear: "", status: "",
    programmes: [],
    loading: true, error: null,
    open: false, editing: null, form: { ...BLANK_FORM },
    saving: false, formError: null
  };

  componentDidMount() {
    this.fetchProgrammes();
    this.fetchAlumni();
  }

  fetchProgrammes = async () => {
    try {
      const res = await api.get("/programmes", { params: { limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ programmes: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchAlumni = async () => {
    this.setState({ loading: true });
    const { page, search, programme, graduationYear, status } = this.state;
    try {
      const res = await api.get("/alumni", {
        params: { page, limit: 20, search: search || undefined, programme: programme || undefined, graduationYear: graduationYear || undefined, status: status || undefined }
      });
      const d = res.data.data || res.data;
      this.setState({
        alumni: d.data || d,
        total: d.total || (d.data || d).length,
        totalPages: d.totalPages || 1,
        loading: false
      });
    } catch (e) {
      this.setState({ error: "Could not load alumni.", loading: false });
    }
  };

  handleFilter = field => e => this.setState({ [field]: e.target.value }, this.fetchAlumni);
  handleSearch = e => this.setState({ search: e.target.value });
  handleSearchSubmit = e => { e.preventDefault(); this.fetchAlumni(); };
  goToPage = p => this.setState({ page: p }, this.fetchAlumni);

  handlePaginationChange = updater => {
    const current = { pageIndex: this.state.page - 1, pageSize: 20 };
    const next = typeof updater === "function" ? updater(current) : updater;
    this.goToPage(next.pageIndex + 1);
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => this.setState({ open: true, editing: null, form: { ...BLANK_FORM }, formError: null });

  openEdit = alumnus => {
    this.setState({
      open: true, editing: alumnus.id, formError: null,
      form: {
        firstName: alumnus.firstName || "",
        lastName: alumnus.lastName || "",
        email: alumnus.email || "",
        phone: alumnus.phone || "",
        programmeId: alumnus.programmeId || "",
        graduationYear: alumnus.graduationYear || "",
        currentEmployer: alumnus.currentEmployer || "",
        jobTitle: alumnus.jobTitle || ""
      }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.firstName || !form.lastName || !form.programmeId || !form.graduationYear) {
      this.setState({ formError: "First name, last name, programme, and graduation year are required." });
      return;
    }
    this.setState({ saving: true, formError: null });
    try {
      if (editing) {
        await api.put(`/alumni/${editing}`, form);
      } else {
        await api.post("/alumni", form);
      }
      this.setState({ open: false, saving: false });
      this.fetchAlumni();
    } catch (e) {
      this.setState({ formError: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleToggleStatus = async (alumnus) => {
    const newStatus = alumnus.status === "active" ? "inactive" : "active";
    try {
      await api.put(`/alumni/${alumnus.id}`, { ...alumnus, status: newStatus });
      this.fetchAlumni();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const { alumni, total, page, loading, error, search, programme, graduationYear, status, programmes, open, editing, form, saving, formError } = this.state;

    const columns = [
      { id: "name", header: "Name", accessorFn: a => `${a.firstName} ${a.lastName}` },
      { id: "programme", header: "Programme", accessorFn: a => a.programme?.name || a.programmeId || "—" },
      { id: "gradYear", header: "Grad Year", accessorFn: a => a.graduationYear || "—" },
      { id: "email", header: "Email", accessorFn: a => a.email || "—" },
      { id: "phone", header: "Phone", accessorFn: a => a.phone || "—" },
      { id: "employer", header: "Employer", accessorFn: a => a.currentEmployer || "—" },
      { id: "jobTitle", header: "Job Title", accessorFn: a => a.jobTitle || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => (
          <Pill tone={info.row.original.status === "inactive" ? "danger" : "success"}>
            {info.row.original.status || "active"}
          </Pill>
        )
      },
      {
        id: "actions",
        header: "",
        cell: info => (
          <div style={{ display: "flex", gap: "4px" }}>
            <Button size="sm" color="info" simple onClick={() => this.openEdit(info.row.original)}>
              <Icon>edit</Icon>
            </Button>
            <Button size="sm" simple onClick={() => this.handleToggleStatus(info.row.original)} title={info.row.original.status === "active" ? "Deactivate" : "Activate"}>
              <Icon>{info.row.original.status === "active" ? "toggle_on" : "toggle_off"}</Icon>
            </Button>
          </div>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar
            tabs={["All Alumni", "Verified"]}
            activeTab={this.state.tab || 0}
            onTabChange={tab => this.setState({ tab })}
            actions={[]}
          />
          <form onSubmit={this.handleSearchSubmit} className={classes.searchRow}>
            <CustomInput
              formControlProps={{ style: { margin: 0, paddingTop: 0, minWidth: "200px" } }}
              inputProps={{
                placeholder: "Search name or email...",
                value: search,
                onChange: this.handleSearch
              }}
            />
            <select className={classes.filterSelect} value={programme} onChange={this.handleFilter("programme")}>
              <option value="">All Programmes</option>
              {programmes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <CustomInput
              formControlProps={{ style: { margin: 0, paddingTop: 0, minWidth: "120px" } }}
              inputProps={{
                placeholder: "Grad Year e.g. 2020",
                value: graduationYear,
                onChange: this.handleFilter("graduationYear"),
                type: "number"
              }}
            />
            <select className={classes.filterSelect} value={status} onChange={this.handleFilter("status")}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <Button size="sm" color="primary" type="submit">Search</Button>
          </form>

          {error && <p style={{ color: "#f44336" }}>{error}</p>}

          <DataTable
            title="Alumni"
            subtitle={`${total} alumnus${total !== 1 ? "es" : ""} found`}
            actions={
              <Button color="white" size="sm" onClick={this.openAdd}>
                <Icon style={{ marginRight: "4px" }}>person_add</Icon> Add Alumnus
              </Button>
            }
            columns={columns}
            data={alumni}
            isLoading={loading}
            emptyState="No alumni found."
            pagination={{ pageIndex: page - 1, pageSize: 20 }}
            onPaginationChange={this.handlePaginationChange}
            totalCount={total}
          />
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Alumnus" : "Add Alumnus"} />
          <DialogContent>
            {formError && <p className={classes.errorMsg}>{formError}</p>}
            <GridContainer>
              <GridItem xs={6}>
                <CustomInput labelText="First Name *" id="alFirstName" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.firstName, onChange: this.handleChange("firstName") }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Last Name *" id="alLastName" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.lastName, onChange: this.handleChange("lastName") }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Email" id="alEmail" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.email, onChange: this.handleChange("email"), type: "email" }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Phone" id="alPhone" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.phone, onChange: this.handleChange("phone") }} />
              </GridItem>
              <GridItem xs={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Programme *</InputLabel>
                  <Select value={form.programmeId} onChange={this.handleChange("programmeId")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Graduation Year *" id="alGradYear" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.graduationYear, onChange: this.handleChange("graduationYear"), type: "number" }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Current Employer" id="alEmployer" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.currentEmployer, onChange: this.handleChange("currentEmployer") }} />
              </GridItem>
              <GridItem xs={6}>
                <CustomInput labelText="Job Title" id="alJobTitle" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.jobTitle, onChange: this.handleChange("jobTitle") }} />
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : editing ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

AlumniList.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(AlumniList));
